import { useEffect, useRef, type RefObject } from 'react';

const useOnClickOutside = <T extends HTMLElement = HTMLElement>(
  ref: RefObject<T>,
  handler: (event: Event) => void,
  events: string[] = ['mousedown', 'touchstart']
): void => {
  const handlerRef = useRef(handler);

  /** Only update the callback when the reference changes */
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const abortController = new AbortController();

    const handleEvent = (event: Event) => {
      const el = ref?.current;

      // Do nothing if clicking ref's element or descendent elements
      if (!el || el.contains(event.target as Node)) {
        return;
      }

      handlerRef.current(event);
    };

    events.forEach((eventType) => {
      document.addEventListener(eventType, handleEvent, { signal: abortController.signal });
    });

    return () => {
      abortController.abort();
    };
  }, [events, ref]);
};

export default useOnClickOutside;
